import { hasRole, PAYROLL_STAFF, R } from "../rbac/access";

const READ_STORAGE_KEY = "navbar_notifications_read_v1";
const ANY_AUTHENTICATED = [R.ADMIN, R.HR, R.MANAGER, R.EMPLOYEE, R.FINANCE];

export function loadReadNotificationIds() {
  try {
    const raw = sessionStorage.getItem(READ_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

export function saveReadNotificationIds(ids) {
  try {
    sessionStorage.setItem(READ_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

/**
 * Build actionable navbar notifications for the current user.
 * Each item includes `path` for react-router navigation on click.
 */
export function buildNavbarNotifications({
  role,
  overview = null,
  pendingLeaveCount = 0,
  readIds = new Set(),
}) {
  const items = [];
  const payroll = overview?.payroll;
  const employees = overview?.employees;
  const periodLabel = payroll?.period_label || "latest period";

  if (hasRole(role, PAYROLL_STAFF) && payroll?.latest_run_id) {
    const processed = payroll.status === "processed" || payroll.lifecycle_status === "locked";
    if (processed) {
      items.push({
        id: "payroll-latest",
        type: "payroll",
        color: "var(--green-600)",
        bg: "var(--green-50)",
        title: "Payroll Processed",
        desc: `${periodLabel} payroll completed for ${payroll.processed_count ?? 0} employees`,
        time: "Recent",
        path: "/payroll-runs",
        roles: PAYROLL_STAFF,
      });
    } else if (payroll.status) {
      items.push({
        id: "payroll-draft",
        type: "payroll",
        color: "var(--blue-600)",
        bg: "var(--blue-50)",
        title: "Payroll In Progress",
        desc: `${periodLabel} run is ${payroll.status} — continue processing`,
        time: "Now",
        path: "/process-payroll",
        roles: PAYROLL_STAFF,
      });
    }
  }

  const normalizedRole = String(role || "").toLowerCase();

  if (normalizedRole === R.MANAGER && pendingLeaveCount > 0) {
    items.push({
      id: "leave-pending-manager",
      type: "leave",
      color: "var(--amber-600)",
      bg: "var(--amber-50)",
      title: "Team leave — manager approval",
      desc: `${pendingLeaveCount} request${pendingLeaveCount === 1 ? "" : "s"} from your team awaiting your approval`,
      time: "Action needed",
      path: "/leave?tab=approval",
      roles: [R.MANAGER],
    });
  }

  if (normalizedRole === R.HR && pendingLeaveCount > 0) {
    items.push({
      id: "leave-pending-hr",
      type: "leave",
      color: "var(--amber-600)",
      bg: "var(--amber-50)",
      title: "Leave — HR approval",
      desc: `${pendingLeaveCount} request${pendingLeaveCount === 1 ? "" : "s"} awaiting HR final approval`,
      time: "Action needed",
      path: "/leave?tab=approval",
      roles: [R.HR],
    });
  }

  if (normalizedRole === R.ADMIN && pendingLeaveCount > 0) {
    items.push({
      id: "leave-pending-admin",
      type: "leave",
      color: "var(--amber-600)",
      bg: "var(--amber-50)",
      title: "Leave approvals",
      desc: `${pendingLeaveCount} open leave request${pendingLeaveCount === 1 ? "" : "s"} need your action`,
      time: "Action needed",
      path: "/leave?tab=approval",
      roles: [R.ADMIN],
    });
  }

  if (hasRole(role, PAYROLL_STAFF)) {
    items.push({
      id: "tds-due",
      type: "tax",
      color: "var(--blue-600)",
      bg: "var(--blue-50)",
      title: "TDS Filing Due",
      desc: "Review TDS summary and statutory deposits",
      time: "Compliance",
      path: "/tds-summary",
      roles: PAYROLL_STAFF,
    });
  }

  const missingBank = employees?.missing_bank_account ?? 0;
  if (hasRole(role, PAYROLL_STAFF) && missingBank > 0) {
    items.push({
      id: "bank-missing",
      type: "alert",
      color: "var(--red-600)",
      bg: "var(--red-50)",
      title: "Missing Bank Accounts",
      desc: `${missingBank} employee${missingBank === 1 ? "" : "s"} have no bank details on file`,
      time: "Setup",
      path: "/employees?module=employees&needs_bank=1&step=4",
      roles: PAYROLL_STAFF,
    });
  }

  if (hasRole(role, [R.ADMIN, R.HR])) {
    items.push({
      id: "attendance-approvals",
      type: "leave",
      color: "var(--teal-600)",
      bg: "var(--teal-50)",
      title: "Attendance approvals",
      desc: "Review regularization and correction requests",
      time: "Workflow",
      path: "/attendance/approvals",
      roles: [R.ADMIN, R.HR],
    });
  }

  if (hasRole(role, [R.MANAGER])) {
    items.push({
      id: "attendance-approvals-manager",
      type: "leave",
      color: "var(--teal-600)",
      bg: "var(--teal-50)",
      title: "Team attendance requests",
      desc: "OT and regularization requests from your team",
      time: "Workflow",
      path: "/attendance/approvals",
      roles: [R.MANAGER],
    });
  }

  if (hasRole(role, [R.EMPLOYEE, R.MANAGER])) {
    items.push({
      id: "my-payslip",
      type: "payroll",
      color: "var(--green-600)",
      bg: "var(--green-50)",
      title: "My Payslip",
      desc: "View or download your monthly payslip",
      time: "Self service",
      path: "/payslip",
      roles: [R.EMPLOYEE, R.MANAGER, R.HR, R.ADMIN],
    });
  }

  if (hasRole(role, [R.EMPLOYEE])) {
    items.push({
      id: "my-attendance",
      type: "leave",
      color: "var(--teal-600)",
      bg: "var(--teal-50)",
      title: "My Attendance",
      desc: "View your monthly calendar and punch times",
      time: "Self service",
      path: "/my-attendance",
      roles: [R.EMPLOYEE],
    });
    items.push({
      id: "my-leave",
      type: "leave",
      color: "var(--amber-600)",
      bg: "var(--amber-50)",
      title: "My Leave",
      desc: "Apply for leave or check your balance",
      time: "Self service",
      path: "/leave?tab=apply",
      roles: [R.EMPLOYEE],
    });
  }

  const seen = new Set();
  return items
    .filter((item) => {
      if (!item.path || seen.has(item.id)) return false;
      seen.add(item.id);
      return hasRole(role, item.roles || ANY_AUTHENTICATED);
    })
    .map((item) => ({
      ...item,
      unread: !readIds.has(item.id),
    }));
}
